/**
 * Hook to fetch stored correlation summaries from correlation_insights_v2
 * Available to ALL users (Standard and Premium) for display purposes only
 * This is UI-level only - does NOT perform any new AI reasoning
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface StoredCorrelationSummary {
  id: string;
  metric_a: string;
  metric_b: string;
  correlation_strength: number;
  correlation_category: string;
  insight_text: string;
  confidence_score: number;
  sample_size: number;
  date_range_start: string;
  date_range_end: string;
  created_at: string;
}

export function useStoredCorrelationSummaries(days: number = 30) {
  return useQuery({
    queryKey: ['stored-correlation-summaries', days],
    queryFn: async (): Promise<StoredCorrelationSummary[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('correlation_insights_v2')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString())
        .order('correlation_strength', { ascending: false })
        .limit(10);

      if (error) {
        console.error('[useStoredCorrelationSummaries] Error fetching:', error);
        return [];
      }

      return (data || []) as StoredCorrelationSummary[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Helper to format metric names for display
 * Converts snake_case to Title Case
 */
export function formatMetricNameForDisplay(metric: string): string {
  return metric
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Helper to get correlation category label
 */
export function getCorrelationCategoryLabel(strength: number): string {
  const absStrength = Math.abs(strength);
  if (absStrength >= 0.7) return 'Strong';
  if (absStrength >= 0.5) return 'Moderate';
  if (absStrength >= 0.3) return 'Weak';
  return 'Very weak';
}
